import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
  Table,
  Space,
  Tooltip,
  message,
  Modal,
  Spin,
  DatePicker,
  Form,
} from "antd";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance, { baseURL } from "../../configs"; // sesuaikan path
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

const defaultPageSize = 20;

export default function BrowsePindahLot() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // filter
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // "view" or "edit"
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState(null); // { header, details }
  const [saving, setSaving] = useState(false);
  const [editForm] = Form.useForm();
  const [editDetails, setEditDetails] = useState([]);

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/pindah-lot", {
        params: {
          page,
          limit: pageSize,
        },
      });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      const total =
        payload.total ||
        payload.count ||
        payload.pagination?.total ||
        rows.length;

      setItems(rows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar pindah lot");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    window.open(`${baseURL}/pindah-lot/export`, "_blank");
  };

  // ====== VIEW / EDIT MODAL ======
  const openModal = async (record, mode) => {
    setModalMode(mode);
    setModalVisible(true);
    setModalLoading(true);
    setModalData(null);
    editForm.resetFields();

    try {
      const res = await axiosInstance.get(
        `/pindah-lot/${record.id_pindah_lot}/with-details`
      );
      const data = res.data || {};
      setModalData(data);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat detail pindah lot");
      setModalVisible(false);
    } finally {
      setModalLoading(false);
    }
  };

  // Set form values AFTER modalData is loaded and form is mounted
  useEffect(() => {
    if (modalData && modalVisible) {
      if (modalMode === "edit") {
        setTimeout(() => {
          editForm.setFieldsValue({
            tanggal_pindah_lot: modalData.header?.tanggal_pindah_lot
              ? dayjs(modalData.header.tanggal_pindah_lot)
              : null,
            catatan: modalData.header?.catatan || "",
          });
        }, 100);
        setEditDetails(modalData.details?.map(d => ({ ...d })) || []);
      } else {
        setEditDetails([]);
      }
    }
  }, [modalData, modalMode, modalVisible, editForm]);

  const handleDetailChange = (index, field, value) => {
    setEditDetails(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleUpdate = async () => {
    if (!modalData?.header) return;

    try {
      const values = await editForm.validateFields();
      setSaving(true);

      await axiosInstance.put(
        `/pindah-lot/${modalData.header.id_pindah_lot}`,
        {
          tanggal_pindah_lot: values.tanggal_pindah_lot
            ? dayjs(values.tanggal_pindah_lot).format("YYYY-MM-DD")
            : null,
          catatan: values.catatan || "",
          details: editDetails.map(d => ({
            id_pindah_lot_detail: d.id_pindah_lot_detail,
            seri: d.seri,
            nopen: d.nopen,
            item_code: d.item_code,
            lot_asal: d.lot_asal,
            jumlah_kemasan_asal: d.jumlah_kemasan_asal,
            lot_tujuan: d.lot_tujuan,
            jumlah_kemasan_tujuan: Number(d.jumlah_kemasan_tujuan) || 0,
          })),
        }
      );

      message.success("Data pindah lot berhasil diupdate");
      setModalVisible(false);
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal mengupdate data pindah lot");
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setModalData(null);
    editForm.resetFields();
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY") : "-";

  // Detail table columns for modal
  const detailColumns = [
    {
      title: "SERI",
      dataIndex: "seri",
      width: 60,
      align: "center",
    },
    {
      title: "NOPEN",
      dataIndex: "nopen",
      width: 120,
    },
    {
      title: "ITEM CODE",
      dataIndex: "item_code",
      width: 160,
      ellipsis: true,
    },
    {
      title: "LOT ASAL",
      dataIndex: "lot_asal",
      width: 100,
    },
    {
      title: "JML KEMASAN ASAL",
      dataIndex: "jumlah_kemasan_asal",
      width: 140,
      align: "right",
    },
    {
      title: "LOT TUJUAN",
      dataIndex: "lot_tujuan",
      width: 110,
      render: (text, record, index) =>
        modalMode === "edit" ? (
          <Input
            size="small"
            value={editDetails[index]?.lot_tujuan ?? text}
            onChange={(e) => handleDetailChange(index, "lot_tujuan", e.target.value)}
            style={{ background: "#fff" }}
          />
        ) : text,
    },
    {
      title: "JML KEMASAN TUJUAN",
      dataIndex: "jumlah_kemasan_tujuan",
      width: 150,
      align: "right",
      render: (text, record, index) =>
        modalMode === "edit" ? (
          <Input
            size="small"
            type="number"
            value={editDetails[index]?.jumlah_kemasan_tujuan ?? text}
            onChange={(e) => handleDetailChange(index, "jumlah_kemasan_tujuan", e.target.value)}
            style={{ background: "#fff", textAlign: "right" }}
          />
        ) : text,
    },
  ];

  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 70,
      align: "center",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "NO PINDAH LOT",
      dataIndex: "no_pindah_lot",
      width: 130,
    },
    {
      title: "TANGGAL MOVING",
      dataIndex: "tanggal_pindah_lot",
      width: 140,
      render: (value) => formatDate(value),
    },
    {
      title: "ALASAN PINDAH",
      dataIndex: "catatan",
      width: 130,
    },
    {
      title: "AKSI",
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              icon={<EyeOutlined style={{ color: "#1BC5BD" }} />}
              onClick={() => openModal(record, "view")}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "#FFA800" }} />}
              onClick={() => openModal(record, "edit")}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Browse Pindah Lot
          </Title>
          <Text type="secondary">
            Kelola daftar pemindahan Lot untuk kebutuhan inventory & kepabeanan.
          </Text>
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{
                background: "#1BC5BD",
                borderColor: "#1BC5BD",
                fontWeight: 600,
              }}
            >
              <Link to="/other/pindah/add" style={{ color: "#fff" }}>
                Tambah
              </Link>
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        style={{ marginTop: 36 }}
        rowKey="id_pindah_lot"
        dataSource={items}
        columns={columns}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `Total ${total} data | ${range[0]}-${range[1]}`,
        }}
        onChange={handleTableChange}
      />

      {/* ====== VIEW / EDIT MODAL ====== */}
      <Modal
        title={
          modalMode === "view"
            ? "Detail Pindah Lot"
            : "Edit Pindah Lot"
        }
        open={modalVisible}
        onCancel={closeModal}
        width={900}
        footer={
          modalMode === "edit"
            ? [
              <Button key="cancel" onClick={closeModal}>
                Batal
              </Button>,
              <Button
                key="update"
                type="primary"
                loading={saving}
                onClick={handleUpdate}
                style={{
                  background: "#1BC5BD",
                  borderColor: "#1BC5BD",
                  fontWeight: 600,
                }}
              >
                Update
              </Button>,
            ]
            : [
              <Button key="close" onClick={closeModal}>
                Tutup
              </Button>,
            ]
        }
      >
        <Spin spinning={modalLoading}>
          {modalData && (
            <>
              {/* Header Info - always use Form */}
              <Form form={editForm} layout="vertical">
                <div
                  style={{
                    background: "#F3F6F9",
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 16,
                  }}
                >
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        No Pindah Lot
                      </Text>
                      <div style={{ fontWeight: 600 }}>
                        {modalData.header?.no_pindah_lot || "-"}
                      </div>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={<Text strong>Tanggal Pindah Lot</Text>}
                        name="tanggal_pindah_lot"
                        style={{ marginBottom: 0 }}
                      >
                        <DatePicker
                          disabled={modalMode === "view"}
                          style={{ width: "100%", background: modalMode === "edit" ? "#fff" : undefined }}
                          format="DD MMMM YYYY"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={<Text strong>Catatan</Text>}
                        name="catatan"
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          disabled={modalMode === "view"}
                          placeholder="Catatan pindah lot"
                          style={{ background: modalMode === "edit" ? "#fff" : undefined }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Form>

              {/* Detail Table */}
              <Text strong style={{ marginBottom: 8, display: "block" }}>
                Detail Barang
              </Text>
              <Table
                rowKey="id_pindah_lot_detail"
                dataSource={modalData.details || []}
                columns={detailColumns}
                size="small"
                pagination={false}
                scroll={{ x: 800 }}
                bordered
              />
            </>
          )}
        </Spin>
      </Modal>
    </Card>
  );
}
